import { useEffect, useRef } from 'react';
import { Box } from '@mui/material';
import { RichTextEditorProvider, RichTextField } from 'mui-tiptap';
import { useEditor } from '@tiptap/react';
import { createFeedbackExtensions } from './richTextExtensions';
import { RichTextToolbar } from './RichTextToolbar';

interface RichTextEditorProps {
  content: string;
  onChange: (html: string) => void;
  onBlur?: () => void;
  placeholder?: string;
  disabled?: boolean;
  enableLinks?: boolean;
  'aria-label'?: string;
}

export function RichTextEditor({
  content,
  onChange,
  onBlur,
  placeholder,
  disabled = false,
  enableLinks = false,
  'aria-label': ariaLabel,
}: RichTextEditorProps) {
  const isInternalUpdate = useRef(false);

  const editor = useEditor({
    extensions: createFeedbackExtensions({ placeholder, enableLinks }),
    content,
    editable: !disabled,
    onUpdate: ({ editor }) => {
      isInternalUpdate.current = true;
      onChange(editor.getHTML());
    },
    onBlur: () => {
      onBlur?.();
    },
  });

  // Sync editor content when prop changes externally (e.g., after 409 cache refresh)
  useEffect(() => {
    if (!editor || editor.isDestroyed) return;
    if (isInternalUpdate.current) {
      isInternalUpdate.current = false;
      return;
    }
    if (editor.getHTML() !== content) {
      editor.commands.setContent(content, { emitUpdate: false });
    }
  }, [content, editor]);

  useEffect(() => {
    if (!editor || editor.isDestroyed) return;
    editor.setEditable(!disabled);
  }, [editor, disabled]);

  if (!editor) {
    return null;
  }

  return (
    <Box
      sx={{
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
        overflow: 'hidden',
        bgcolor: disabled ? 'action.disabledBackground' : 'background.paper',
      }}
      aria-label={ariaLabel}
      role={ariaLabel ? 'textbox' : undefined}
    >
      <RichTextEditorProvider editor={editor}>
        {!disabled && <RichTextToolbar showLinkButton={enableLinks} />}
        <RichTextField
          variant="standard"
          sx={{
            '& .MuiInput-root::before, & .MuiInput-root::after': {
              display: 'none',
            },
            '& .MuiTiptap-FieldContainer-root': {
              minHeight: 120,
              py: 3,
              px: 2,
            },
            '& .ProseMirror': {
              minHeight: 100,
              outline: 'none',
              px: 2,
              wordBreak: 'break-word',
              overflowWrap: 'anywhere',
              '& .is-empty::before': {
                color: 'text.secondary',
                pointerEvents: 'none',
              },
            },
          }}
        />
      </RichTextEditorProvider>
    </Box>
  );
}

export default RichTextEditor;
